import utils from "../../share/util/utils";

// 以下DTCステータス
const DIAG_STATUS_CONFIRM = 'fixed_resource.g_status_vehicle_dtc_status_confirm';
const DIAG_STATUS_PENDING = 'fixed_resource.i_status_vehicle_dtc_status_tentative';
const DIAG_STATUS_PERMANENT = 'fixed_resource.m_status_vehicle_dtc_status_permanent';
const DIAG_STATUS_NO_DTC = 'message.j_status_vehicle_dtc_status_no_dtc';
const DIAG_STATUS_CANNOT_CONNECT = 'message.k_status_vehicle_dtc_status_cannot_connect';

/**
 * 一覧に表示する為のDTCTableを作成する
 * @param {array} dtcSystems DTC情報List
 * @param {string} systemId システムID
 * @param {string} systemName システム名
 * @param {object} root 呼び出し元のオブジェクト(this)
 * @returns {object} テーブル情報
 */
export const createObdDtcTable = (dtcSystems, systemId, systemName, root) => {
  // DTC が 0 個の場合も DTC 無しと表示する
  if (dtcSystems.length <= 0) {
    const dtcInfo = {
      system: systemId,
      dtc_status: 'no_dtc',
    };
    const noDtcTableItem = createEmptyObdDtcTableItem(dtcInfo, systemName, root);
    return [noDtcTableItem];
  }

  // DTCなし・通信不能・DTC取得非対応の情報が含まれる場合は、取り出す
  const emptySystems = dtcSystems.filter(dtc =>
    dtc.dtc_status === 'no_dtc' ||
    dtc.dtc_status === 'cannot_connect'
  );

  // DTCなし・通信不能・DTC取得非対応の情報が含まれていない情報も取り出しておく
  const notEmptySystems = dtcSystems.filter(dtc =>
    dtc.dtc_status !== 'no_dtc' &&
    dtc.dtc_status !== 'cannot_connect'
  );

  let createTableItem = [];
  // DTCなし・通信不能システムの場合は、コード以降を表示しない
  if (emptySystems.length > 0) {
    emptySystems.map((system) => {
      createTableItem.push(createEmptyObdDtcTableItem(system, systemName, root));
    });
  }

  // DTCなし・通信不能・DTC取得非対応以外の要素は通常のテーブルに変換する
  if (notEmptySystems.length > 0) {
    const tableItem = computeObdDTCTableItem(notEmptySystems, systemName, root);
    createTableItem = createTableItem.concat(tableItem);
  }

  return createTableItem;
};


/**
 * OBD DTC共通システムテーブルの作成
 * @param {array} dtcList DTC 情報
 * @param {string} systemName システム名
 * @param {object} root root
 * @returns {object} テーブル情報
 */
const computeObdDTCTableItem = (dtcList, systemName, root) => {
  // テーブル情報がない場合は続行しない
  if (!dtcList) return [];
  return dtcList.map(e => ({
    systemID: e.system,                                       // FFD 一覧取得のためのキー
    name: systemName,                                         // システム名
    status: getDtcStatusResource(e.dtc_status, root),         // DTC 状態
    dtc: utils.isNullOrDefault(e.code, '-'),                  // DTC コード
    description: getDtcDescriptionResourceEcu(e, root),       // 説明・故障部位
    igonCount: '-',                                           // IGON 回数
    igonTime: '-',                                            // IGON 経過時間
    counterType: 'message.common_hyphen',                     // 種別
    buttonVisible: e.has_ffd,                                 // FFD 一覧取得のボタン表示・非表示
  }));
};

/**
 * OBD DTC 情報非表示を表すテーブルアイテムを作成する
 * @param {array} dtcList DTC 情報
 * @param {string} systemName システム名
 * @param {object} root root
 * @returns {object} テーブル情報
 */
const createEmptyObdDtcTableItem = (dtc, systemName, root) => {
  return {
    systemID: dtc.system,                                   // FFD 一覧取得のためのキー
    name: systemName,                                       // システム名
    status: getDtcStatusResource(dtc.dtc_status, root),     // DTC 状態
    dtc: '-',                                               // DTC コード
    description: 'message.common_hyphen',                   // 説明・故障部位
    igonCount: '-',                                         // IGON 回数
    igonTime: '-',                                          // IGON 経過時間
    counterType: 'message.common_hyphen',                   // 種別
    buttonVisible: false,                                   // FFD 一覧取得のボタン表示・非表示
  };
};

/**
 * OBD オンボードモニタテスト DTCテーブルアイテムの作成
 * @param {object[]} dtcList DTC情報(取得した計測データ)
 * @param {boolean} isUds J1979-2ならtrue
 * @param {object} root 呼び出し元コンポーネント
 * @param {boolean} addBgStyle bgStyleをdtc-cursor-defaultに設定する場合true
 * @returns {object} DTCテーブルアイテム
 */
export const computeObdOmtDTCTableItem = (dtcList, isUds, root, addBgStyle = false) => {
  // テーブル情報がない場合は続行しない
  if (!dtcList) return [];
  return dtcList.map((e) => ({
    ...e,
    dtc: e.code, // 共通ソート関数を利用するために設定
    unit: getObdUnit(e.unit, root),
    description: isUds ? getDtcDescriptionResourceEcu(e, root) : undefined, // 共通ソート関数を利用するためmessageIDのまま
    ...(addBgStyle && { bgStyle: 'dtc-cursor-default' })
  }));
};

// DTCの状態を設定
export const getDtcStatusResource = (status, root) => {
  let dtcStatus = status;
  switch (status) {
    // Confirmed
    case 'confirmed':
      dtcStatus = root.$t(DIAG_STATUS_CONFIRM); break;
    // Pending
    case 'pending':
      dtcStatus = root.$t(DIAG_STATUS_PENDING); break;
    // Confirmed/Pending
    case 'confirmed_pending':
      dtcStatus = `${root.$t(DIAG_STATUS_CONFIRM)}/${root.$t(DIAG_STATUS_PENDING)}`; break;
    // Permanent
    case 'permanent':
      dtcStatus = root.$t(DIAG_STATUS_PERMANENT); break;
    //ダイアグコードなし
    case 'no_dtc':
      dtcStatus = root.$t(DIAG_STATUS_NO_DTC); break;
    //通信不能
    case 'cannot_connect':
      dtcStatus = root.$t(DIAG_STATUS_CANNOT_CONNECT); break;
    default: break;
  }
  return dtcStatus;
};

/**
 * 診断テーブルの説明・故障部位カラムの設定(ECUによる分岐あり)
 * @param {object} res 判定対象
 * @param {object} arg 呼び出し元
 * @returns {string} 引き当て結果
 */
const getDtcDescriptionResourceEcu = (res, arg) => {
  // 説明引き当て対象
  let baseId = 'dtc.' + res.protocol_type + '_' + res.code;
  if (arg.$te(baseId)) return baseId;
  // ECUによってDTCの説明が分かれる場合を対象とする
  baseId = 'dtc.' + res.protocol_type + '_' + res.code + '_' + res.reference;
  if (arg.$te(baseId)) return baseId;
  // 引き当て対象ではない場合は「指定外コード」とする
  return 'message.status_vehicle_dtc_description_unspecified';
};

/**
 * 単位の引き当て
 * @param {string} unit 判定対象
 * @param {object} arg 呼び出し元
 * @returns {string} 単位
 */
const getObdUnit = (unit, arg) => {
  // 引き当て対象
  if (arg.$te(`obd_unit.${unit}`)) return arg.$t(`obd_unit.${unit}`);
  return arg.$t(`message.common_hyphen`);
};

/**
 * メモリクリアの結果メッセージを生成する
 * @param {object} component Vue の Component (this) を指定
 * @param {Array<string>} failureClearSystemNames 失敗したシステム名の配列
 * @returns {string} 生成したメッセージ
 */
export const getMemoryClearResultMessage = (component, failureClearSystemNames) => {
  const removeHeader = component.$t('fixed_resource.obd_function_dtcclear_message_result_ok');
  const removeOperation = '<span style="color: red; font-size: 16px;">' + component.$t('fixed_resource.obd_function_dtcclear_confirm_message_result_ok') + '</span>';
  const removeFooter = component.$t('fixed_resource.obd_function_dtcclear_message_result_annotation');
  if (failureClearSystemNames?.length > 0) {
    const slashSystemName = failureClearSystemNames.join('/');
    const removeFailure = component.$t('fixed_resource.obd_function_dtcclear_message_result_failure', [slashSystemName]);
    return ('{0}\r\n\r\n{1}\r\n\r\n{2}\r\n\r\n{3}').format(removeHeader, removeFailure, removeOperation, removeFooter);
  } else {
    return ('{0}\r\n\r\n{1}\r\n\r\n{2}').format(removeHeader, removeOperation, removeFooter);
  }
};

/**
 * OBD レディネスステータステーブルの作成
 * @param {array} readinessStatusList レディネスステータス情報
 * @param {object} root root
 * @returns {object} テーブル情報
 */
export const createObdReadinessStatusItems = (readinessStatusList, root) => {
  // テーブル情報がない場合は続行しない
  if (!readinessStatusList) return [];
  return readinessStatusList.map(e => ({
    readinessGroup: `readiness_group.${e.readiness_group}`,  // レディネスグループ
    status: e.dtc_status,                                             // DTC 状態
    dtc: utils.isNullOrDefault(e.code, '-'),                          // DTC コード
    description: getDtcDescriptionResourceEcu(e, root),               // 説明・故障部位
  }));
};

/**
 * OBD 診断頻度テーブルの作成
 * @param {array} diagnoseFrequencyList 診断頻度情報
 * @param {object} root root
 * @returns {object} テーブル情報
 */
export const createObdDiagnoseFrequencyItems = (diagnoseFrequencyList, root) => {
  // テーブル情報がない場合は続行しない
  if (!diagnoseFrequencyList) return [];
  return diagnoseFrequencyList.map(e => ({
    status: e.dtc_status,                                             // DTC 状態
    dtc: utils.isNullOrDefault(e.code, '-'),                           // DTC コード
    description: getDtcDescriptionResourceEcu(e, root),                  // 説明・故障部位
    molecule: e.molecule === '' ? '-' : e.molecule,                   // 分子
    denominator: e.denominator === '' ? '-' : e.denominator,          // 分母
    mar: e.mar === '' ? '-' : e.mar                                   // MAR
  }));
};
